import { mergeDeepLeft, assocPath } from 'ramda';
import memoize from 'memoize-async';
import { acquireToken } from '@alixpartners/ui-utils';
import ApolloClient from 'apollo-boost';
import { BlockBlobClient } from '@azure/storage-blob';
import { DEFAULT_RETRIES, serializeExceedParams } from 'utils/api';

import { handleApiError } from 'components/ApiErrorNotification';
import { navigateToAccessDenied } from '@alixpartners/ui-components';

import { getApiUrl, serializeQueryParams } from 'utils/api';
import convertDetailsChartData from 'utils/detailsChartDataConverter';
import { wheelToByCategoryConverter } from 'utils/wheelToByCategoryConverter';
import { SPEND_TYPE } from 'Data/constants';
import { pmiHeroChartDataConverter } from './pmiHeroChartDataConverter';
import { pmiTreeChartDataConverter } from './pmiTreeChartDataConverter';
import { getCookie } from './cookies';

export const DEMO_MODE_COOKIE_NAME = 'demo_mode_cookie';
export const isDemoMode = !!getCookie(DEMO_MODE_COOKIE_NAME);

export function uploadExcelFile(file, onFileCreated) {
	return createExcelFile(file)
		.then(({ url, id }) => {
			onFileCreated(id);
			return { url, id };
		})
		.then(({ url, id }) =>
			uploadFile(url, file).then(() => updateExcelFile({ id }))
		);
}

export function validateFirstFile(file, isDirect) {
	return createFirstFile(file, isDirect).then(({ url, id }) =>
		uploadFile(url, file).then(() => validateFile(id, isDirect))
	);
}

export function validateFirstVendorFile(file, isDirect) {
	return createFirstVendorFile(file).then(({ url, id }) =>
		uploadFile(url, file).then(() => validateVendorFile(id, isDirect))
	);
}
export function validateNormalizedFile(file) {
	return createFirstNormalizedFile(file).then(({ url, id }) =>
		uploadFile(url, file).then(() => validateNormalizingFile(id))
	);
}

export function validateCategoryFile(file) {
	return createCategoryFile(file).then(({ url, id }) =>
		uploadFile(url, file).then(() => validateCategoryFilev2(id, url))
	);
}

export function validateSegmentationFile(file) {
	return createSegmentationFile(file).then(
		({ url }) =>
			uploadFile(url, file).then(() => {
				return url;
			})
		// .then(({url}) =>
		// addSegmentationData(url)
		// )
	);
}

// https://gist.github.com/niyazpk/f8ac616f181f6042d1e0
export function updateUrlParameter(uri, key, value) {
	const i = uri.indexOf('#');
	const hash = i === -1 ? '' : uri.substr(i);
	uri = i === -1 ? uri : uri.substr(0, i);

	const re = new RegExp('([?&])' + key + '=.*?(&|$)', 'i');
	const separator = uri.indexOf('?') !== -1 ? '&' : '?';

	if (uri.match(re)) {
		uri = uri.replace(re, '$1' + key + '=' + value + '$2');
	} else {
		uri = uri + separator + key + '=' + value;
	}

	return uri + hash;
}

export const updateUrlToDemoMode = url => {
	if (isDemoMode) {
		url = updateUrlParameter(url, 'demoMode', isDemoMode);
	}

	return url;
};

export function getRequest(url, options = {}) {
	let defaults = { method: 'GET' };
	url = updateUrlToDemoMode(url);

	return requestWithToken(url, defaults, options);
}

export function postRequest(url, options = {}, skipDefaultOptions) {
	let defaults = { method: 'POST' };
	url = updateUrlToDemoMode(url);

	return requestWithToken(url, defaults, options, skipDefaultOptions);
}

export function putRequest(url, options = {}) {
	let defaults = { method: 'PUT' };
	url = updateUrlToDemoMode(url);

	return requestWithToken(url, defaults, options);
}

export function patchRequest(url, options = {}) {
	let defaults = { method: 'PATCH' };
	url = updateUrlToDemoMode(url);

	return requestWithToken(url, defaults, options);
}

export function deleteRequest(url, options = {}) {
	let defaults = { method: 'DELETE' };
	url = updateUrlToDemoMode(url);

	return requestWithToken(url, defaults, options);
}

export const requestWithRetry = (request, retries) => {
	const currentRetries = retries !== undefined ? retries : DEFAULT_RETRIES;

	return request().catch(e => {
		if (
			currentRetries > 0 &&
			e &&
			e.message &&
			e.message.includes('Failed to fetch')
		) {
			return requestWithRetry(request, currentRetries - 1);
		}

		throw e;
	});
};

function requestWithToken(url, defaults, options, skipDefaultOptions) {
	let mergedOptions = {};

	const request = () =>
		acquireToken()
			.then(token => {
				if (!token) {
					throw new Error(`ServerError (401)`);
				}
				const authDefaults = addAuthHeaders(token, defaults);
				mergedOptions = mergeDeepLeft(authDefaults, options) || {};
				return jsonRequest(url, mergedOptions, skipDefaultOptions);
			})
			.catch(e => {
				if (isDemoMode && mergedOptions.method !== 'GET') {
					handleApiError(url.split('?')[0], true);
				}

				if (e.response) {
					if (e.response.status === 403) {
						navigateToAccessDenied(e.aadUrl, e.aadName);
					} else if (e.correlationId) {
						handleApiError(e.correlationId);
					}
				}

				throw e;
			});

	return requestWithRetry(request);
}

function addAuthHeaders(token, options = {}) {
	return assocPath(['headers', 'Authorization'], `Bearer ${token}`, options);
}

export async function getGraphQLClientWithToken() {
	const token = await acquireToken();

	const { REACT_APP_API_URL } = process.env;

	if (!token) {
		throw new Error(`ServerError (401)`);
	}

	const authDefaults = addAuthHeaders(token);
	const client = new ApolloClient({
		onError({ graphQLErrors, networkError }) {
			if (graphQLErrors) {
				handleApiError(
					graphQLErrors[0]
						? graphQLErrors[0].message
						: 'Error in graphQL request'
				);
			} else if (networkError) {
				handleApiError(
					'A network error has occurred. Please check out your connection.'
				);
			}
		},
		uri: `//${REACT_APP_API_URL}/graphql/${isDemoMode ? '?demoMode=true' : ''}`,
		...authDefaults,
	});

	return client;
}
export async function getGraphQLClientForDAndIWithToken() {
	const token = await acquireToken();

	const { REACT_APP_API_URL } = process.env;

	if (!token) {
		throw new Error(`ServerError (401)`);
	}

	const authDefaults = addAuthHeaders(token);
	const client = new ApolloClient({
		onError({ graphQLErrors, networkError }) {
			if (graphQLErrors) {
				handleApiError(
					graphQLErrors[0]
						? graphQLErrors[0].message
						: 'Error in graphQL request'
				);
			} else if (networkError) {
				handleApiError(
					'A network error has occurred. Please check out your connection.'
				);
			}
		},
		uri: `//${REACT_APP_API_URL}/d-and-i/supplier-diversity/`,
		...authDefaults,
	});

	return client;
}
export async function getGraphQLClientSpendControllerBaseline() {
	const token = await acquireToken();

	const { REACT_APP_API_URL } = process.env;

	if (!token) {
		throw new Error(`ServerError (401)`);
	}

	const authDefaults = addAuthHeaders(token);
	const client = new ApolloClient({
		onError({ graphQLErrors, networkError }) {
			if (graphQLErrors) {
				handleApiError(
					graphQLErrors[0]
						? graphQLErrors[0].message
						: 'Error in graphQL request'
				);
			} else if (networkError) {
				handleApiError(
					'A network error has occurred. Please check out your connection.'
				);
			}
		},
		uri: `//${REACT_APP_API_URL}/spend-graphql`,
		...authDefaults,
	});

	return client;
}
export async function getGraphQLClientCapitalMarketUpdateData() {
	const token = await acquireToken();

	const { REACT_APP_API_URL } = process.env;

	if (!token) {
		throw new Error(`ServerError (401)`);
	}

	const authDefaults = addAuthHeaders(token);
	const client = new ApolloClient({
		onError({ graphQLErrors, networkError }) {
			if (graphQLErrors) {
				handleApiError(
					graphQLErrors[0]
						? graphQLErrors[0].message
						: 'Error in graphQL request'
				);
			} else if (networkError) {
				handleApiError(
					'A network error has occurred. Please check out your connection.'
				);
			}
		},
		uri: `//${REACT_APP_API_URL}/working-capital`,
		...authDefaults,
	});

	return client;
}
export async function getGraphQLClientForMultiSourceSkuWithToken() {
	const token = await acquireToken();

	const { REACT_APP_API_URL } = process.env;

	if (!token) {
		throw new Error(`ServerError (401)`);
	}

	const authDefaults = addAuthHeaders(token);
	const client = new ApolloClient({
		onError({ graphQLErrors, networkError }) {
			if (graphQLErrors) {
				handleApiError(
					graphQLErrors[0]
						? graphQLErrors[0].message
						: 'Error in graphQL request'
				);
			} else if (networkError) {
				handleApiError(
					'A network error has occurred. Please check out your connection.'
				);
			}
		},
		uri: `//${REACT_APP_API_URL}/sku-graphql/`,
		...authDefaults,
	});

	return client;
}

export async function getGraphQLClientForSkuAnalysisWithToken() {
	const token = await acquireToken();

	const { REACT_APP_API_URL } = process.env;

	if (!token) {
		throw new Error(`ServerError (401)`);
	}

	const authDefaults = addAuthHeaders(token);
	const client = new ApolloClient({
		onError({ graphQLErrors, networkError }) {
			if (graphQLErrors) {
				handleApiError(
					graphQLErrors[0]
						? graphQLErrors[0].message
						: 'Error in graphQL request'
				);
			} else if (networkError) {
				handleApiError(
					'A network error has occurred. Please check out your connection.'
				);
			}
		},
		uri: `//${REACT_APP_API_URL}/sku-graphql/`,
		...authDefaults,
	});

	return client;
}

export async function getGraphQLClientForPivotWithToken() {
	const token = await acquireToken();

	const { REACT_APP_API_URL } = process.env;

	if (!token) {
		throw new Error(`ServerError (401)`);
	}

	const authDefaults = addAuthHeaders(token);
	const client = new ApolloClient({
		onError({ graphQLErrors, networkError }) {
			if (graphQLErrors) {
				handleApiError(
					graphQLErrors[0]
						? graphQLErrors[0].message
						: 'Error in graphQL request'
				);
			} else if (networkError) {
				handleApiError(
					'A network error has occurred. Please check out your connection.'
				);
			}
		},
		uri: `//${REACT_APP_API_URL}/pivotgraphql/`,
		...authDefaults,
	});

	return client;
}
export async function getGraphQLClientForVendorLibraryWithToken() {
	const token = await acquireToken();

	const { REACT_APP_API_URL } = process.env;

	if (!token) {
		throw new Error(`ServerError (401)`);
	}

	const authDefaults = addAuthHeaders(token);
	const client = new ApolloClient({
		onError({ graphQLErrors, networkError }) {
			if (graphQLErrors) {
				handleApiError(
					graphQLErrors[0]
						? graphQLErrors[0].message
						: 'Error in graphQL request'
				);
			} else if (networkError) {
				handleApiError(
					'A network error has occurred. Please check out your connection.'
				);
			}
		},
		uri: `//${REACT_APP_API_URL}/APVendorDB/apVendor-graphql/`,
		...authDefaults,
	});

	return client;
}
export async function getGraphQLClientForSpendCubeDataComponentWithToken() {
	const token = await acquireToken();

	const { REACT_APP_API_URL } = process.env;

	if (!token) {
		throw new Error(`ServerError (401)`);
	}

	const authDefaults = addAuthHeaders(token);
	const client = new ApolloClient({
		onError({ graphQLErrors, networkError }) {
			if (graphQLErrors) {
				handleApiError(
					graphQLErrors[0]
						? graphQLErrors[0].message
						: 'Error in graphQL request'
				);
			} else if (networkError) {
				handleApiError(
					'A network error has occurred. Please check out your connection.'
				);
			}
		},
		uri: `//${REACT_APP_API_URL}/dc-graphql`,
		...authDefaults,
	});

	return client;
}
export async function getGraphQLClientAPVendorDBWithToken() {
	const token = await acquireToken();

	const { REACT_APP_API_URL } = process.env;

	if (!token) {
		throw new Error(`ServerError (401)`);
	}

	const authDefaults = addAuthHeaders(token);
	const client = new ApolloClient({
		onError({ graphQLErrors, networkError }) {
			if (graphQLErrors) {
				handleApiError(
					graphQLErrors[0]
						? graphQLErrors[0].message
						: 'Error in graphQL request'
				);
			} else if (networkError) {
				handleApiError(
					'A network error has occurred. Please check out your connection.'
				);
			}
		},
		uri: `//${REACT_APP_API_URL}/APVendorDB/apVendor-graphql/`,
		...authDefaults,
	});

	return client;
}

// vendor-mapping
export async function getGraphQLClientVendorMappingWithToken() {
	const token = await acquireToken();

	const { REACT_APP_API_URL } = process.env;

	if (!token) {
		throw new Error(`ServerError (401)`);
	}

	const authDefaults = addAuthHeaders(token);
	const client = new ApolloClient({
		onError({ graphQLErrors, networkError }) {
			if (graphQLErrors) {
				handleApiError(
					graphQLErrors[0]
						? graphQLErrors[0].message
						: 'Error in graphQL request'
				);
			} else if (networkError) {
				handleApiError(
					'A network error has occurred. Please check out your connection.'
				);
			}
		},
		uri: `//${REACT_APP_API_URL}/pivotgraphql/`, //change the uri for the data fetching for table
		...authDefaults,
	});

	return client;
}

function jsonRequest(url, options = {}, skipDefaultOptions) {
	const defaultOptions = !skipDefaultOptions
		? {
				headers: { 'content-type': 'application/json' },
		  }
		: {};

	return fetch(url, mergeDeepLeft(defaultOptions, options))
		.then(response => {
			if (!response.ok) {
				return response.text().then(textResponse => {
					try {
						const jsonResponse = JSON.parse(textResponse);
						return Promise.reject({ ...jsonResponse, response });
					} catch (_e) {
						return Promise.reject(textResponse);
					}
				});
			}

			return response;
		})
		.then(response => response.text())
		.then(textResponse => {
			try {
				const jsonResponse = JSON.parse(textResponse);
				return jsonResponse;
			} catch (_e) {
				return textResponse;
			}
		})
		.catch(error => {
			try {
				return Promise.reject(error);
			} catch (_e) {
				throw new Error(error);
			}
		});
}

export function createExcelFile(file) {
	const url = getApiUrl('/spend/excel');
	const options = {
		method: 'POST',
		body: JSON.stringify(file),
	};

	return postRequest(url, options);
}

export function createFirstFile(file, isDirect) {
	const direction = isDirect ? SPEND_TYPE.DIRECT : SPEND_TYPE.INDIRECT;
	const url = getApiUrl(`/data-classification/${direction}`);
	const options = {
		method: 'POST',
		body: JSON.stringify(file),
	};

	return postRequest(url, options);
}

export function createFirstVendorFile(file) {
	const url = getApiUrl(`/data-classification/fileupload`);
	const options = {
		method: 'POST',
		body: JSON.stringify(file),
	};
	return postRequest(url, options);
}
export function createFirstNormalizedFile(file) {
	const url = getApiUrl(`/data-classification/fileupload/normalized`);
	const options = {
		method: 'POST',
		body: JSON.stringify(file),
	};
	return postRequest(url, options);
}
export function createCategoryFile(file) {
	const url = getApiUrl(`/category-management-v3/fileupload`);
	const options = {
		method: 'POST',
		body: JSON.stringify(file),
	};
	return postRequest(url, options);
}

export function createSegmentationFile(file) {
	const url = getApiUrl(`/category-management/fileupload`);
	const options = {
		method: 'POST',
		body: JSON.stringify(file),
	};
	return postRequest(url, options);
}
export function classifyFile(id, isDirect) {
	const direction = isDirect ? SPEND_TYPE.DIRECT : SPEND_TYPE.INDIRECT;
	const url = getApiUrl(`/data-classification/v2/${direction}/${id}`);

	return patchRequest(url);
}

export function validateFile(id, isDirect) {
	const direction = isDirect ? SPEND_TYPE.DIRECT : SPEND_TYPE.INDIRECT;
	const url = getApiUrl(`/data-classification/${id}/validate/${direction}`);

	const options = {
		method: 'POST',
	};

	return postRequest(url, options);
}

export function validateVendorFile(id, isDirect) {
	const payload = {
		isDirect: isDirect,
	};
	const url = getApiUrl(`/data-classification/${id}/validate-new`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}
export function validateNormalizingFile(id) {
	const url = getApiUrl(`/data-classification/validate-normalized/${id}`);
	const options = {
		method: 'POST',
	};
	return postRequest(url, options);
}
export function validateCategoryFilev2(id, fileUrl) {
	const payload = {
		id: id,
		url: fileUrl,
	};
	const url = getApiUrl(`/category-management-v3/execute-sp-pipeline`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}
export function saveEditedVendorData(editedData) {
	const url = getApiUrl('/category-management/update-vendor-segmentation');
	const options = {
		method: 'POST',
		body: JSON.stringify(editedData),
	};
	return postRequest(url, options);
}
export function deleteVendorOption(option) {
	const url = getApiUrl(
		'/category-management/delete-vendor-segmentation-dropdown'
	);
	const options = {
		method: 'POST',
		body: JSON.stringify([option]),
	};
	return postRequest(url, options);
}
export function deleteContractOption(option) {
	const url = getApiUrl('/category-management/delete-contract-dropdown');
	const options = {
		method: 'POST',
		body: JSON.stringify([option]),
	};
	return postRequest(url, options);
}

// export function daleteVendorData() {
// 	const url = getApiUrl(`/data-classification/delete-data`);
// 	const options = {
// 		method: 'GET',
// 	};
// 	return getRequest(url, options);
// }
export function deleteCategoryData() {
	const url = getApiUrl(`/category-management-v3/truncate-category`);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function getNormalizationDataFlag() {
	const url = getApiUrl(`/vendor-normalization/get-direct-indirect-flag`);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function daleteVendorFiles(data) {
	const url = getApiUrl(`/vendor-normalization/delete-file`);
	const options = {
		method: 'POST',
		body: JSON.stringify(data),
	};
	return postRequest(url, options);
}

export function getClassificationColumns(id) {
	const url = getApiUrl(`/data-classification/${id}/classification-columns`);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

/**
 * Upload data using the Azure SDK to ensure that large files
 * are uploaded in chunks when files are larger than 256mb.
 *
 * @param {string} url
 * @param {blob} file
 */
export function uploadFile(url, file) {
	const blobClient = new BlockBlobClient(url);
	return blobClient.uploadData(file);
}

export function updateExcelFile(file) {
	const url = getApiUrl(`/spend/excel/${file.id}`);
	const options = {
		method: 'PATCH',
		body: JSON.stringify(file),
	};

	return patchRequest(url, options);
}

export function parseResponse(textResponse) {
	try {
		const jsonResponse = JSON.parse(textResponse);
		return jsonResponse;
	} catch (_e) {
		return textResponse;
	}
}

export function getNonAdressableData() {
	const url = getApiUrl('/rawdata/nonaddressable');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getFileExampleUrl() {
	const url = getApiUrl('/data-classification/uploadExampleUrl');

	return url;
}

export function getNormalizationTemplate() {
	const url = getApiUrl('/vendor-normalization/download-template');
	return url;
}

export function getSKUTemplate() {
	const url = getApiUrl('/sku/download-template');
	return url;
}

export function getDandIClientDataTemplate() {
	const url = getApiUrl('/d-and-i/download-template');
	return url;
}

export function downloadSegmentationData(year) {
	const url = getApiUrl('/category-management/download');
	const options = {
		method: 'POST',
		body: JSON.stringify(year),
	};
	return postRequest(url, options);
}

export function getSpendByBusinessUnit(bodyParam) {
	const url = getApiUrl(`/category-management/get-spendby-businessunit`);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}
export function getCustomChart(bodyParam) {
	const url = getApiUrl(`/category-management/custom-chart-data`);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getKpisForCategoryManagement(bodyParam) {
	const url = getApiUrl(`/category-management/get-kpis`);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};

	return postRequest(url, options);
}

export function getTopVendorsData(bodyParam) {
	const url = getApiUrl(`/category-management/get-top-vendor-spend`);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getSpendBySubcategory(bodyParam) {
	const url = getApiUrl(`/category-management/spend-by-subcategories`);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getSpendByCostCenter(bodyParam) {
	const url = getApiUrl(`/category-management/get-spendby-costcenter`);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getSpendByRegion(categoryType, categoryName) {
	const data = { categoryType, categoryName };
	const url = getApiUrl(`/category-management/get-spendby-region`);
	const options = {
		method: 'POST',
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
}

export function getAverageSpendBySubCategory(bodyParam) {
	const url = getApiUrl(
		`/category-management/avg-vendor-spendby-sub-categories`
	);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getCategoryInsightSettings() {
	const url = getApiUrl('/category-management/insights/get-settings');
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function getInsightCardsSettings() {
	const url = getApiUrl('/category-management/insights/card-settings');
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function updateInsightCardsSettings(bodyParam) {
	const url = getApiUrl('/category-management/insights/update-card-setting');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getDropdownCategoryVendors(bodyParam) {
	const url = getApiUrl('/category-management/insights/dropdown-vendors');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function updateCategoryInsightSettings(settings, name) {
	const url = getApiUrl(
		`/category-management/insights/update-settings?key=${name}`
	);
	const options = {
		method: 'POST',
		body: JSON.stringify(settings),
	};
	return postRequest(url, options);
}

export function getSpendByCountry(bodyParam) {
	const url = getApiUrl(`/category-management/spend-by-country`);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getVendorCountByCategory(bodyParam) {
	const url = getApiUrl(
		`/category-management/vendor-count-by-category-and-spend`
	);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getDPO(bodyParam) {
	const url = getApiUrl(`/category-management/get-spendby-dpo`);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getVendorPareto(bodyParam) {
	const url = getApiUrl(`/category-management/pareto-chart-by-vendor`);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function saveCharterData(charterData) {
	const url = getApiUrl(`/category-management/save-chart-data`);
	const options = {
		method: 'POST',
		body: JSON.stringify(charterData),
	};
	return postRequest(url, options);
}

export function getCharterData(categoryName) {
	const url = getApiUrl(`/category-management/get-chart-data`);
	const options = {
		method: 'POST',
		body: JSON.stringify(categoryName),
	};
	return postRequest(url, options);
}

export function fetchWheelChartData({ addCompaniesData, ...params }) {
	if (addCompaniesData) {
		params.dimensions = ['spendType', ...params.dimensions];
		params.disableTailSpendSegments = true;
		params.splitSpendByDimension = 'company';
	}

	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(`/spend/data?${serializedParams}`);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function fetchTailSpendData(params, data) {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(`/spend/data?${serializedParams}`);
	const options = {
		method: 'POST',
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
}

function makeAPIParams(params, extraParams = false) {
	const {
		dateFrom,
		dateTo,
		hierarchy,
		filters = [],
		filterValues = [],
		includeCustomCategories,
		currency,
		showNonSourceable = true,
		harmonized,
		storedQueryId,
	} = params;

	const apiParams = {
		dateFrom,
		dateTo,
		hierarchy,
		filters: [...filters, 'sourceable'],
		filterValues: [...filterValues, !showNonSourceable],
		includeCustomCategories,
		harmonized,
		storedQueryId,
	};

	if (params.skip !== undefined && params.take !== undefined) {
		apiParams.skip = params.skip;
		apiParams.take = params.take;
	}

	if (params.sortDirection !== undefined && params.sortIndex !== undefined) {
		apiParams.sortDirection = params.sortDirection;
		apiParams.sortIndex = params.sortIndex;
	}

	if ('getTailSpend' in params) {
		apiParams.getTailSpend = params.getTailSpend;
	}

	if (currency) {
		apiParams.currency = currency;
		apiParams.currencyType = 'fixed';
	}

	if (extraParams) {
		for (const k in params) {
			if (!(k in apiParams)) {
				apiParams[k] = params[k];
			}
		}
	}

	return apiParams;
}

export async function getSpendByCategoryChartData(params) {
	const apiParams = makeAPIParams(params);
	const serializedParams = serializeQueryParams(apiParams);
	const url = getApiUrl(`/spend/overview/v2/bysuppliers?${serializedParams}`);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function createStoredQuery(params) {
	return postRequest(getApiUrl('/storedqueries'), {
		method: 'POST',
		body: JSON.stringify(params),
	});
}

function getStoredQuery(storedQueryId) {
	const url = getApiUrl(`/storedqueries/${storedQueryId}`);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}
const memoizedGetStoredQuery = memoize(getStoredQuery);
let q = Promise.resolve();
export const fetchStoredQuery = function(params) {
	return (q = q.then(() => memoizedGetStoredQuery(params)));
};

export function getSpendByDimentionChartData(params) {
	const apiParams = makeAPIParams(params);
	apiParams.dimensions = params.dimensions;

	const serializedParams = serializeQueryParams(apiParams);
	const url = getApiUrl(
		`/spend/overview/v2/byspecificdimension?${serializedParams}`
	);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export async function getInitatives(params) {
	const serializedParams = serializeQueryParams(params);

	const url = getApiUrl(`/initiatives?${serializedParams}`);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export async function getInitativeCreators(id) {
	const url = getApiUrl(`/initiatives/creators`);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}
export async function getInitative(id) {
	const url = getApiUrl(`/initiatives/${id}`);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export async function createInitative(data) {
	const url = getApiUrl('/initiatives');
	const options = {
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
}

export async function deleteInitative(id) {
	const url = getApiUrl(`/initiatives/${id}`);

	return deleteRequest(url);
}

export async function patchInitative(id, data) {
	const url = getApiUrl(`/initiatives/${id}`);
	const options = {
		body: JSON.stringify(data),
	};
	return patchRequest(url, options);
}

export function getAvailableDimentionsData() {
	const url = getApiUrl(`/spend/overview/v2/availabledimensions`);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export const defaultDimensions = [
	'category',
	'category',
	'category',
	'category',
	'category',
];

const TAXONOMY_DIMENSIONS = ['category', 'clientCategory'];

function getWheelAllChartData(params) {
	const {
		dateFrom,
		dateTo,
		currency,
		dimensions,
		filters = [],
		filterValues = [],
		harmonized,
		storedQueryId,
		addCompaniesData,
		isPmi,
	} = params;

	const apiParams = {
		dimensions: dimensions || defaultDimensions,
		dateFrom,
		dateTo,
		filters: [...filters, 'sourceable'],
		filterValues: [...filterValues, 'false'],
		harmonized,
		storedQueryId,
		addCompaniesData,
		isPmi,
	};

	if (currency) {
		apiParams.currency = currency;
		apiParams.currencyType = 'fixed';
	}

	return Promise.all([
		fetchWheelChartData(apiParams),
		fetchWheelChartData({
			...apiParams,
			filterValues: [...filterValues, 'true'],
		}),
	]).then(([nonAddressable, addressable]) => {
		return {
			nonAddressableData: nonAddressable.data,
			addressableData: addressable.data,
		};
	});
}

const memoizedApi = { getWheelAllChartData: memoize(getWheelAllChartData) };

export const getWaterfallChartDataFromWheel = params => {
	const {
		categoryName,
		categoryLevel,
		hierarchy,
		apiCacheTrigger,
		dimensions,
		isTailSpend,
		othersThreshold,
		showAllCategories,
		showAllSegments,
		segmentTabIndex,
		segments,
		showNegativeSpend,
		showNetSpend,
		...apiParams
	} = params;

	apiParams.dimensions = dimensions;

	return memoizedApi
		.getWheelAllChartData(apiParams, apiCacheTrigger)
		.then(wheelChartData => {
			if (params.addCompaniesData) {
				let convertedWaterfallChartData = pmiHeroChartDataConverter.convert(
					wheelChartData,
					params
				);

				return convertedWaterfallChartData;
			}

			let convertedWaterfallChartData = convertDetailsChartData(
				wheelChartData,
				params
			);

			return convertedWaterfallChartData;
		});
};

export const generateHeroChartDataFile = params => {
	const serializedParams = serializeQueryParams(params);

	const url = getApiUrl(`/analysisexport/hero?${serializedParams}`);

	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
};

export const getSpendCategorizationChartDataFromWheel = params => {
	const {
		categoryName,
		categoryLevel,
		hierarchy,
		apiCacheTrigger,
		dimensions,
		isTailSpend,
		othersThreshold,
		showAllCategories,
		showAllSegments,
		segmentTabIndex,
		segments,
		...apiParams
	} = params;

	apiParams.dimensions =
		dimensions && TAXONOMY_DIMENSIONS.includes(dimensions[0])
			? dimensions
			: defaultDimensions;

	return memoizedApi
		.getWheelAllChartData(apiParams, apiCacheTrigger)
		.then(wheelChartData => {
			if (params.addCompaniesData) {
				let convertedWaterfallChartData = pmiTreeChartDataConverter.convert(
					wheelChartData,
					params
				);

				return convertedWaterfallChartData;
			}

			let convertedSpendCategorizationChartData = wheelToByCategoryConverter.convertData(
				wheelChartData,
				params
			);

			return convertedSpendCategorizationChartData;
		});
};

export function getWaterfallChartData(params = {}) {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(`/spend/overview/waterfall?${serializedParams}`);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getSpendEditData(params) {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(`/spend/edit?${serializedParams}`);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getUnpublishedData() {
	const url = getApiUrl(`/spend/edit/unpublished`);

	return getRequest(url);
}

export function updateUnpublishedData(data = {}) {
	const url = getApiUrl(`/spend/edit/unpublished`);
	const options = {
		body: JSON.stringify(data),
	};

	return putRequest(url, options);
}

export function publishData() {
	const url = getApiUrl(`/spend/edit/publish`);

	return postRequest(url);
}

export function updateSpendEditData(data = {}) {
	const url = getApiUrl('/spend/edit');
	const options = {
		body: JSON.stringify(data),
	};

	return putRequest(url, options);
}
export function updateNormilizationEditData(data = {}) {
	const url = getApiUrl('/spend/edit/normalization');
	const options = {
		body: JSON.stringify(data),
	};

	return putRequest(url, options);
}

// vendor-mapping update api
export function updateVendorMappingEditData(data = {}) {
	const url = getApiUrl('/VendorMapping/update-vendor');
	const options = {
		body: JSON.stringify(data),
	};

	return putRequest(url, options);
}
export function getClassificationOverrideRules() {
	const url = getApiUrl('/classificationoverrides');

	return getRequest(url);
}

export function createUpdateClassificationOverrideRule(data = {}) {
	const url = getApiUrl('/classificationoverrides');
	const options = {
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
}

export function createClassificationOverrideErrorReport(data) {
	const url = getApiUrl('/classificationoverrides/changerequest');
	const options = {
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
}

export function deleteUpdateClassificationOverrideRule(data = {}) {
	const { id } = data;
	const url = getApiUrl(`/classificationoverrides/${id}/softdelete`);

	return postRequest(url);
}

export function getHarmonizedData() {
	const url = getApiUrl('/spend/edit/harmonized_suppliers');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getTaxonomies(params) {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(`/taxonomy?${serializedParams}`);

	return getRequest(url);
}

export function getTaxonomyReleaseNotes() {
	const url = getApiUrl('/taxonomy/releasenotes/');

	return getRequest(url);
}

export function upgradeTaxonomy() {
	const url = getApiUrl('/taxonomy/upgrade');

	return postRequest(url);
}

export function updateSpendType(params, data) {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(`/spend/edit?${serializedParams}`);
	const options = {
		body: JSON.stringify(data),
	};

	return patchRequest(url, options);
}

export function moveToBin(params) {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(`/TransactionsBin/move?${serializedParams}`);

	return postRequest(url);
}

export function moveMultipleToBin(params, data) {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(`/TransactionsBin/movemultiple?${serializedParams}`);

	const options = {
		body: JSON.stringify(data),
	};
	return postRequest(url, options);
}

export function getTransactionsBin(params) {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(`/TransactionsBin/?${serializedParams}`);

	return getRequest(url);
}

export function getTransactionsBinDimensions() {
	const url = getApiUrl(`/TransactionsBin/dimensions`);

	return getRequest(url);
}

export function deleteTransactions(data) {
	const options = {
		body: JSON.stringify(data),
	};

	const url = getApiUrl(`/TransactionsBin/delete`);

	return postRequest(url, options);
}

export function restoreTransactions(data) {
	const options = {
		body: JSON.stringify(data),
	};
	const url = getApiUrl(`/TransactionsBin/restore`);

	return postRequest(url, options);
}

export function getSpendCategorizationChartData(params = {}) {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(`/spend/overview/bycategory?${serializedParams}`);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getByCompanyData(params = {}) {
	const apiParams = makeAPIParams(params);
	const serializedParams = serializeQueryParams(apiParams);
	const url = getApiUrl(`/spend/overview/bycompany?${serializedParams}`);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getByDimensionData(params = {}) {
	const apiParams = makeAPIParams(params, true);
	const serializedParams = serializeQueryParams(apiParams);
	const url = getApiUrl(`/spend/overview/bydimension?${serializedParams}`);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getSpendSettingsChartData() {
	const url = getApiUrl('/spend/overview/settings');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getAliasNameData() {
	const url = getApiUrl('/benchmark/metadata');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getRandomAlias(value) {
	const url = getApiUrl(`/benchmark/aliases/${value}`);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getBenchmarkCategoryData(params) {
	const url = getApiUrl('/benchmark/get-categorytree');
	const options = {
		method: 'POST',
		body: JSON.stringify(params),
	};

	return postRequest(url, options);
}

export function saveBenchmarkSettings(params) {
	const url = getApiUrl('/benchmark/save-settings');
	const options = {
		method: 'POST',
		body: JSON.stringify(params),
	};

	return postRequest(url, options);
}

export function saveAliasMetadata(params) {
	const url = getApiUrl('/benchmark/update-data');
	const options = {
		method: 'POST',
		body: JSON.stringify(params),
	};
	return postRequest(url, options);
}

export function getRevenueYear() {
	const url = getApiUrl('/benchmark/get-revenue');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function saveRevenueYear(params) {
	const url = getApiUrl('/benchmark/save-revenue');
	const options = {
		method: 'POST',
		body: JSON.stringify(params),
	};
	return postRequest(url, options);
}

export function exportBenchmarkData(bodyParam) {
	const url = getApiUrl('/benchmark/export');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getTailSpendData(params) {
	let url = getApiUrl(`/rawdata/chart`);

	if (params) {
		const searchParams = new URLSearchParams();
		Object.keys(params).forEach(key => {
			const value = params[key];
			if (value != null && value !== '') {
				searchParams.append(key, value);
			}
		});
		url = getApiUrl(`/rawdata/chart?${searchParams.toString()}`);
	}

	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getTailSpendAnalysisData(params) {
	let url = getApiUrl(`/rawdata/tailspendanalysis`);

	if (params) {
		const { segments, ...restParams } = params;
		const searchParams = new URLSearchParams();

		Object.keys(restParams).forEach(key => {
			const value = params[key];

			if (value != null && value !== '') {
				searchParams.append(key, value);
			}
		});

		url = getApiUrl(`/rawdata/tailspendanalysis?${searchParams.toString()}`);

		if (segments && segments.length) {
			let segmentsQuery = '';

			segments.forEach(({ name, start, end }, i) => {
				segmentsQuery = `${segmentsQuery}&segments[${i}].Name=${encodeURIComponent(
					name
				)}`;
				segmentsQuery = `${segmentsQuery}&segments[${i}].Start=${start}`;
				segmentsQuery = `${segmentsQuery}&segments[${i}].End=${end}`;
			});

			url = `${url}${segmentsQuery}`;
		}
	}

	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getFileArchiveData() {
	const url = getApiUrl('/rawdata/filearchive');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getVendorFileArchiveData() {
	const url = getApiUrl('/rawdata/final-filearchive');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getCategoryFileArchiveData() {
	const url = getApiUrl(
		'/category-management-v3/get-category-management-files'
	);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function cleanVendorState() {
	const url = getApiUrl('/data-classification/clear-state');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}
export function cleanCategoryState() {
	const url = getApiUrl(
		'/category-management-v3/clear-category-management-state'
	);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}
export function getCompanyMetadata() {
	const url = getApiUrl('/companyMetadata');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getCompanyMetadataOptions() {
	const url = getApiUrl('/companyMetadata/options');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function saveCompanyMetadata(data) {
	const url = getApiUrl('/companyMetadata');
	const options = {
		method: 'POST',
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
}
export function deleteFromAccessControl(data) {
	const url = getApiUrl('/rawdata/delete-access-control-data');
	const options = {
		method: 'DELETE',
		body: JSON.stringify(data),
	};

	return deleteRequest(url, options);
}

export function pushToBenchMarkDB(bodyParam) {
	const url = getApiUrl(`/benchmark/save-and-trigger`);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};

	return postRequest(url, options);
}

export function pushToApVendorDB(bodyParam) {
	const url = getApiUrl(`/apvendordb/trigger-pipeline`);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};

	return postRequest(url, options);
}

export function getPipelineStatus() {
	const url = getApiUrl('/benchmark/pipeline-status');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getApVendorDBPipelineStatus() {
	const url = getApiUrl('/apvendordb/pipeline-running-status');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function runNormalizationPipeline(data = {}) {
	const url = getApiUrl('/normalization-pipeline');
	const options = {
		method: 'POST',
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
}
export function exportNormalizationCSVEXPORT() {
	const url = getApiUrl(
		'/data-classification/normalizedbloburl/normalized.csv'
	);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getPipelinePublishStatus() {
	const url = getApiUrl('/benchmark/pipeline-publish-status');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getApVendorDBPushDetails() {
	const url = getApiUrl('/apvendordb/push-details');
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function getApVendorDBClassificationThreshold() {
	const url = getApiUrl('/apvendordb/get-settings');
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function updateApVendorDBClassificationThreshold(bodyParam) {
	const url = getApiUrl('/apvendordb/update-settings');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function updateApVendorDBClassificationPreference(bodyParam) {
	const url = getApiUrl('/apvendordb/update-classification');
	const options = {
		body: JSON.stringify(bodyParam),
	};
	return putRequest(url, options);
}

export function getApVendorDBClassifcationUpdatingStatus() {
	const url = getApiUrl('/apvendordb/update-status');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getCompaniesMetadata() {
	const url = getApiUrl('/companiesmetadata');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}
export function getCMSidebarConfig(payload) {
	const url = getApiUrl(
		`/category-management/get-sidebar-config?taxonomyType=${
			payload.taxonomyType
		}`
	);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getGridData(year) {
	const url = getApiUrl('/category-management/vendor-segmentation');
	const options = {
		method: 'POST',
		body: JSON.stringify(year),
	};
	return postRequest(url, options);
}
export function getSegmentationYear() {
	const url = getApiUrl('/category-management/vendor-segmentation-get-year');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}
export function getVendorSegmentationData() {
	const url = getApiUrl(
		'/category-management/get-vendor-segmentation-dropdown'
	);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function getContractData() {
	const url = getApiUrl('/category-management/get-contract-dropdown');
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}
export function updateVendorSegmentation(updatedOptions) {
	const url = getApiUrl(
		'/category-management/update-vendor-segmentation-dropdown'
	);
	const options = {
		method: 'POST',
		body: JSON.stringify(updatedOptions),
	};
	return postRequest(url, options);
}
export function updateVendorContract(updatedOptions) {
	const url = getApiUrl('/category-management/update-contract-dropdown');
	const options = {
		method: 'POST',
		body: JSON.stringify(updatedOptions),
	};
	return postRequest(url, options);
}

export function getCommonVendors(bodyParam) {
	const url = getApiUrl('/category-management/insights/common-vendors-bu');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getPaymentTerms(bodyParam) {
	const url = getApiUrl('/category-management/insights/payment-terms');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getOutofPolicy(bodyParam) {
	const url = getApiUrl('/category-management/insights/out-policy');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getFragmentedCategories(bodyParam) {
	const url = getApiUrl('/category-management/insights/fragmented');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getWellManagedCategories(bodyParam) {
	const url = getApiUrl('/category-management/insights/wellmanaged');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getBenchmarkSpend(bodyParam) {
	const url = getApiUrl('/category-management/insights/benchmark-spend');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function deleteCategory(categoryid) {
	const url = getApiUrl('/category-management/delete-sidebar-tree');
	const options = {
		method: 'POST',
		body: JSON.stringify(categoryid),
	};
	return postRequest(url, options);
}

export function getBenchmarkRevenue(bodyParam) {
	const url = getApiUrl('/category-management/insights/benchmark-revenue');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getPCard(bodyParam) {
	const url = getApiUrl('/category-management/insights/p-card');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getSignificantGrowthSuppliers(bodyParam) {
	const url = getApiUrl('/category-management/insights/significant-growth');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getNonPreferdSuppliers(bodyParam) {
	const url = getApiUrl('/category-management/insights/non-preferred');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getSupplierSegmentation(bodyParam) {
	const url = getApiUrl('/category-management/insights/supplier-segmentation');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

// Category Management -> Temp labour
export function getTemplabourFilters() {
	const url = getApiUrl('/category-management-v3/filters');
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function getBLSgrowthChartData(bodyParam) {
	const url = getApiUrl('/category-management-v3/charts/bls-growth');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getBLSCityMapData(bodyParam) {
	const url = getApiUrl('/category-management-v3/charts/BLSCity');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getBLSStateMapData(bodyParam) {
	const url = getApiUrl('/category-management-v3/charts/BLSState');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getMarkupData(bodyParam) {
	const url = getApiUrl('/category-management-v3/charts/client-mark-up');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getBoxWhiskerClientData(bodyParam) {
	const url = getApiUrl('/category-management-v3/charts/client-box-whisker');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getBoxWhiskerData(bodyParam) {
	const url = getApiUrl('/category-management-v3/charts/BLSBoxWhisker');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getSpendBreakdown(bodyParam) {
	const url = getApiUrl('/category-management-v3/charts/spend-overview');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getSpendByPaycode(bodyParam) {
	const url = getApiUrl('/category-management-v3/charts/ap-paycode');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}
export function getTopState(bodyParam) {
	const url = getApiUrl('/category-management-v3/charts/top-10-states');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}
export function getAvgMarginBySupplierData(bodyParam) {
	const url = getApiUrl(
		'/category-management-v3/charts/w-avg-margin-by-supplier'
	);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getAvgMarginByJobTitleData(bodyParam) {
	const url = getApiUrl('/category-management-v3/charts/job-titles');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getAvgMarginByGrossSpendByGroupData(bodyParam) {
	const url = getApiUrl('/category-management-v3/charts/group');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

//For chart in Category Management -> Dashboard
export function getCategorySpendGrowth(bodyParam) {
	const url = getApiUrl('/category-management/insights/spend-growth');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getSpendOverTimeChart(bodyParam) {
	const url = getApiUrl('/category-management/spend-over-time');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}
export function getPaymentTermBySupplierData(bodyParam) {
	const url = getApiUrl('/category-management/payment-terms-suppliers');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}
export function getSupplierClassificationData(bodyParam) {
	const url = getApiUrl('/category-management/supplier-classification');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getSpendComparisonByYear(bodyParam) {
	const url = getApiUrl('/category-management/spend-comparision-by-year');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}
export function getFiltersForSpendByMonth(bodyParam) {
	const url = getApiUrl('/category-management/filters');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}
export function getSpendComparisonByMonth(bodyParam) {
	const url = getApiUrl('/category-management/mom-spendchart');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getContractCoveragePercentData(bodyParam) {
	const url = getApiUrl('/category-management/contract-coverage');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function updateCompaniesMetadata(data) {
	const url = getApiUrl('/companiesmetadata');
	const options = {
		method: 'POST',
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
}

export function getTailSpend() {
	const url = getApiUrl('/rawdata/tailspend');

	return getRequest(url);
}

export function getSpendCubeSettings() {
	const url = getApiUrl('/rawdata/config');

	return getRequest(url);
}
export function getColumnWithData() {
	const url = getApiUrl('/rawdata/get-portfolio-options');
	return getRequest(url);
}

export function getPorfolioCompanies() {
	const url = getApiUrl('/rawdata/portfolio-companies');

	return getRequest(url);
}

export function getSettingsOptions() {
	const url = getApiUrl('/rawdata/config/options');

	return getRequest(url);
}
export function getPortcoTraces() {
	const url = getApiUrl('/rawdata/get-portco-trace');
	return getRequest(url);
}

export function getSettingsOthersThreshold() {
	const url = getApiUrl('/rawdata/config/othersThreshold');

	return getRequest(url);
}

export function getAccessControlInfo() {
	const url = getApiUrl('/rawdata/get-accesscontrol-data');

	return getRequest(url);
}

export function getPCCompanies() {
	const url = getApiUrl('/rawdata/get-parent-child-companies');

	return getRequest(url);
}

export function saveAccessControl(data) {
	const url = getApiUrl('/rawdata/portfolio-insert-or-update');
	const options = {
		method: 'POST',
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
}

export function deleteAllData() {
	const url = getApiUrl('/spend/data/clean-up');

	return postRequest(url);
}

export function getTotalSpend(params) {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(`/spend/data/totalspend?${serializedParams}`);
	return getRequest(url);
}

export function saveSettings(data) {
	const url = getApiUrl('/rawdata/config');
	const options = {
		method: 'POST',
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
}

export function getWidgetsList() {
	const url = getApiUrl('/widgets');

	return getRequest(url);
}

export function getWidgetData(path) {
	const url = getApiUrl(path);

	return getRequest(url);
}

export function getClassificationSettings() {
	const url = getApiUrl('/data-classification/config');

	return getRequest(url);
}

export function getSpendOverTimeSeries(params) {
	const { filters = [], filterValues = [], showNonSourceable } = params;

	params = {
		...params,
		filters: [...filters, 'sourceable'],
		filterValues: [...filterValues, !showNonSourceable],
	};

	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(`/spend/timeseries/data?${serializedParams}`);

	return getRequest(url);
}

export function saveClassificationSettings(data) {
	const url = getApiUrl('/data-classification/config');
	const options = {
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
}
export function saveSideBarConfig(data) {
	const url = getApiUrl('/category-management/update-sidebar-config');
	const options = {
		method: 'POST',
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
}

export function saveTailSpendAnalysis(data) {
	const url = getApiUrl('/tailspendanalysis/config');
	const options = {
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
}

export function generateTailSpendAnalysisFile() {
	const url = getApiUrl('/tailspendanalysis/transactions');

	return getRequest(url);
}

export function getTailSpendAnalysis() {
	const url = getApiUrl('/tailspendanalysis/config');

	return getRequest(url);
}

export function getTailSpendAnalysisPreview(params) {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(`/tailspendanalysis/preview?${serializedParams}`);

	return getRequest(url);
}

export function deleteTailSpendAnalysis() {
	const url = getApiUrl('/tailspendanalysis/config');

	return deleteRequest(url);
}

export const markNotificationsRead = ids => {
	const url = getApiUrl('/notifications/my');
	const options = {
		body: JSON.stringify({
			newState: 'Read',
			ids: Array.isArray(ids) ? ids : [ids],
		}),
	};

	return patchRequest(url, options);
};

export const archiveNotifications = ids => {
	const url = getApiUrl('/notifications/my');
	const options = {
		body: JSON.stringify({
			newState: 'Archived',
			ids: Array.isArray(ids) ? ids : [ids],
		}),
	};

	return patchRequest(url, options);
};

export const getNotifications = () => {
	const url = getApiUrl('/notifications/my');

	return getRequest(url);
};

export const generateSpendCube = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/spend/excel/transactions${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getPeriodsInfo = () => {
	const url = getApiUrl('/spend/excel/periods');

	return getRequest(url);
};

export const createOpportunity = data => {
	const url = getApiUrl('/trackandopps');
	const options = {
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
};

export const getShareOfTotalSpend = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/analysis/shareoftotalspend${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const getShareOfTotalSpendsku = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/analysis/shareoftotalspendsku${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const getSkuDualSourcedData = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/analysis/dual-source-sku${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getSpendOverTime = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/pmi/spendovertime${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getRegionalMapData = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/pmi/regionalmap${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getInvestigateRegionalMapData = async params => {
	const serializedParams = await serializeExceedParams(
		params,
		'pivotingFilters',
		9
	);
	const url = getApiUrl(
		`/analysis/regionalmap${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getRegionsData = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/pmi/regions${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getInvestigateRegionsData = async params => {
	const serializedParams = await serializeExceedParams(
		params,
		'pivotingFilters',
		9
	);
	const url = getApiUrl(
		`/analysis/regions${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getLocationsData = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/pmi/regions/data${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getInvestigateLocationsData = async params => {
	const serializedParams = await serializeExceedParams(
		params,
		'pivotingFilters',
		9
	);
	const url = getApiUrl(
		`/analysis/regions/data${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getRegionalTailSpend = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/pmi/regionaltailspend${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getRegionalMapDataExport = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/pmiexport/regionalmap${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getInvestigateRegionalMapDataExport = async params => {
	const serializedParams = await serializeExceedParams(
		params,
		'pivotingFilters',
		9
	);
	const url = getApiUrl(
		`/analysisexport/regionalmap${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const getCompaniesSpendComparison = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/pmi/companiesspendcomparison${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const getCompaniesComparisonInfo = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/pmi/companiescomparisoninfo${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const exportSpendByVendorChart = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/pmiexport/spendsharebyvendorschart${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const exportSpendByVendors = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/pmiexport/spendsharebyvendors${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const exportSpendOvertime = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/pmiexport/spendovertime${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const exportRegionsData = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/pmiexport/regions/data${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const exportInvestigateRegionsData = async params => {
	const serializedParams = await serializeExceedParams(
		params,
		'pivotingFilters',
		9
	);
	const url = getApiUrl(
		`/analysisexport/regions/data${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const exportTreeData = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/pmiexport/tree${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const exportInvestigateTreeData = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/analysisexport/tree${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

const putToKeyValueStorage = params => {
	const { key, data } = params;

	const url = getApiUrl(`/KeyValueStorage/${key}`);

	const options = {
		body: `'${JSON.stringify(data)}'`,
	};

	return postRequest(url, options);
};

const getFromKeyValueStorage = async params => {
	const { key } = params;
	const url = getApiUrl(`/KeyValueStorage/${key}`);

	const data = await getRequest(url);

	try {
		if (typeof data !== 'object') {
			return JSON.parse(data) || [];
		}
	} catch (e) {
		return [];
	}

	return data;
};

const getStorageKeys = async () => {
	const url = getApiUrl(`/KeyValueStorage/keys`);

	const data = await getRequest(url);

	return data;
};

const TAX_MAPPER_STORAGE_KEY = 'tax_mapper_storage_key';
const TAX_MAPPER_APPLIED_STORAGE_KEY = 'tax_mapper_applied_storage_key';
const INDIRECT_TAX_MAPPER_STORAGE_KEY = 'indirect_tax_mapper_storage_key';
const INDIRECT_TAX_MAPPER_APPLIED_STORAGE_KEY =
	'indirect_tax_mapper_applied_storage_key';

export const saveTaxonomyMapperData = data => {
	return putToKeyValueStorage({ data, key: TAX_MAPPER_STORAGE_KEY });
};

export const fetchTaxonomyMapperData = async () => {
	const keys = await getStorageKeys();

	if (!keys || !keys.includes(TAX_MAPPER_STORAGE_KEY)) {
		return [];
	}

	return getFromKeyValueStorage({ key: TAX_MAPPER_STORAGE_KEY });
};

export const saveIndirectTaxonomyMapperData = (data, taxonomyVersion) => {
	return putToKeyValueStorage({
		data,
		key: INDIRECT_TAX_MAPPER_STORAGE_KEY + taxonomyVersion,
	});
};

export const fetchIndirectTaxonomyMapperData = async taxonomyVersion => {
	const keys = await getStorageKeys();

	if (
		!keys ||
		!keys.includes(INDIRECT_TAX_MAPPER_STORAGE_KEY + taxonomyVersion)
	) {
		return [];
	}

	return getFromKeyValueStorage({
		key: INDIRECT_TAX_MAPPER_STORAGE_KEY + taxonomyVersion,
	});
};

export const saveAppliedTaxonomyMapperData = data => {
	return putToKeyValueStorage({
		data,
		key: TAX_MAPPER_APPLIED_STORAGE_KEY,
	});
};
export const saveAppliedIndirectTaxonomyMapperData = (
	data,
	taxonomyVersion
) => {
	return putToKeyValueStorage({
		data,
		key: INDIRECT_TAX_MAPPER_APPLIED_STORAGE_KEY + taxonomyVersion,
	});
};

export const fetchAppliedTaxonomyMapperData = async () => {
	const keys = await getStorageKeys();

	if (!keys || !keys.includes(TAX_MAPPER_APPLIED_STORAGE_KEY)) {
		return [];
	}

	return getFromKeyValueStorage({ key: TAX_MAPPER_APPLIED_STORAGE_KEY });
};

export const fetchAppliedIndirectTaxonomyMapperData = async taxonomyVersion => {
	const keys = await getStorageKeys();

	if (
		!keys ||
		!keys.includes(INDIRECT_TAX_MAPPER_APPLIED_STORAGE_KEY + taxonomyVersion)
	) {
		return [];
	}

	return getFromKeyValueStorage({
		key: INDIRECT_TAX_MAPPER_APPLIED_STORAGE_KEY + taxonomyVersion,
	});
};

export const getPreviewDirectCompanyDetails = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/projecttaxonomy/direct/companydetails${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const getPreviewIndirectCompanyDetails = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/projecttaxonomy/indirect/companydetails${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const getCompanyAnonymizedMapping = () => {
	const url = getApiUrl('/projecttaxonomy/anonymizedmapping');

	return getRequest(url);
};

export const getAppState = () => {
	const url = getApiUrl('/data-classification/state');

	return getRequest(url);
};

export function applyTaxonomyDirect(params) {
	return postRequest(getApiUrl('/projecttaxonomy/applytaxonomy'), {
		method: 'POST',
		body: JSON.stringify(params),
	});
}

export function applyTaxonomyIndirect(params) {
	return postRequest(getApiUrl('/projecttaxonomy/indirect/applytaxonomy'), {
		method: 'POST',
		body: JSON.stringify(params),
	});
}

export const getProjectTaxonomies = spendType => {
	const url = getApiUrl(`/projecttaxonomy/${spendType}/taxonomies`);

	return getRequest(url);
};

export const getProjectTaxonomiesIndirectClient = spendType => {
	const url = getApiUrl(`/projecttaxonomy/indirect/taxonomies-client`);

	return getRequest(url);
};

export const getTransactionsCount = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/projecttaxonomy/transactions/count${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const changeCategorisation = (spendType, params, data) => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/projecttaxonomy/${spendType}/categorization${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);
	const options = {
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
};
export const changeCategorisationIndirectClient = (spendType, params, data) => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/projecttaxonomy/indirect/client-categorization${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);
	const options = {
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
};

export const createInitiative = data => {
	const url = getApiUrl('/initiatives');
	const options = {
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
};

export const updateInitiative = data => {
	const url = getApiUrl(`/initiatives/${data.id}`);
	const options = {
		body: JSON.stringify(data),
	};

	return putRequest(url, options);
};

export const assignInitiativeToWave = data => {
	const url = getApiUrl(`/initiatives/assign`);

	const options = {
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
};

export const getOptimizationCategories = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/initiatives/optimizationcategories${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const getHarmonizationVendors = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/initiatives/harmonizationvendors${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const getWaves = () => {
	const url = getApiUrl('/waves');

	return getRequest(url);
};

export const getTopCommonVendors = params => {
	const serializedParams = serializeQueryParams(params);

	const url = getApiUrl(
		`/pmi/topcommonvendors${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getInvestigateTopCommonVendors = async params => {
	const serializedParams = await serializeExceedParams(
		params,
		'pivotingFilters',
		9
	);

	const url = getApiUrl(
		`/analysis/topcommonvendors${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const getCommonVendorsData = params => {
	const serializedParams = serializeQueryParams(params);

	const url = getApiUrl(
		`/pmi/commonvendors/data${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getInvestigateCommonVendorsData = async params => {
	const serializedParams = await serializeExceedParams(
		params,
		'pivotingFilters',
		9
	);

	const url = getApiUrl(
		`/analysis/commonvendors/data${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const createWave = () => {
	const url = getApiUrl('/waves');

	return postRequest(url);
};

export const deleteWave = id => {
	const url = getApiUrl(`/waves/${id}`);

	return deleteRequest(url);
};

export const exportTopCommonVendors = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/pmiexport/topcommonvendorschart${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const exportInvestigateTopCommonVendors = async params => {
	const serializedParams = await serializeExceedParams(
		params,
		'pivotingFilters',
		9
	);
	const url = getApiUrl(
		`/analysisexport/topcommonvendorschart${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const exportCommonVendorsData = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/pmiexport/commonvendorstable${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const exportInvestigateCommonVendorsData = async params => {
	const serializedParams = await serializeExceedParams(
		params,
		'pivotingFilters',
		9
	);
	const url = getApiUrl(
		`/analysisexport/commonvendorstable${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const getBudget = () => {
	const url = getApiUrl('/budget');

	return getRequest(url);
};

export const updateBudget = data => {
	const url = getApiUrl('/budget');

	const options = {
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
};

export const deleteBudget = id => {
	const url = getApiUrl(`/budget/${id}`);

	return deleteRequest(url);
};

export const deleteAllBudget = () => {
	const url = getApiUrl('/budget');

	return deleteRequest(url);
};

export function importBudget(formData, isAppend) {
	const url = getApiUrl(`/budget/upload${isAppend ? '/append' : ''}`);
	const options = {
		method: 'POST',
		body: formData,
	};

	return postRequest(url, options, true);
}

export const getBudgetSavings = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/budget/savings${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getBudgetDimensions = () => {
	const url = getApiUrl('/budget/dimensions');

	return getRequest(url);
};

export const getBudgetSearchOptions = () => {
	const url = getApiUrl('/budget/search/values');

	return getRequest(url);
};

export const getBudgetVariance = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/budget/variance${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getBudgetDataExport = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/budget/export${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getBudgetTableExport = () => {
	const url = getApiUrl('/budget/export/table');

	return getRequest(url);
};

export const getBudgetFilters = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/budget/filters${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getBudgetPerformance = params => {
	const serializedParams = serializeQueryParams(params);

	const url = getApiUrl(
		`/budget/performance${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getBudgetSummary = params => {
	const serializedParams = serializeQueryParams(params);

	const url = getApiUrl(
		`/budget/summary${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const updateTailSpendSettings = data => {
	const url = getApiUrl('/rawdata/config/tailspendsettings');

	const options = {
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
};

export const getTopVendors = params => {
	const serializedParams = serializeQueryParams(params);

	const url = getApiUrl(
		`/analysis/topvendors${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getInitiativeTopVendors = initiativeId => {
	const url = getApiUrl(`/initiatives/${initiativeId}/topvendors`);

	return getRequest(url);
};

export const getInitiativeItemTopVendors = params => {
	const serializedParams = serializeQueryParams(params);

	const url = getApiUrl(
		`/initiatives/item/topvendors${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const getSearchResults = params => {
	const serializedParams = serializeQueryParams(params);

	const url = getApiUrl(
		`/spend/data/search${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getExcelDimensions = () => {
	const url = getApiUrl('/spend/excel/dimensions');

	return getRequest(url);
};

export const getExcelTaxonomy = () => {
	const url = getApiUrl('/spend/excel/spendtaxonomy');

	return getRequest(url);
};

export const getTopBreakdownItems = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/analysis/topbreakdownitems${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const getMainChart = async params => {
	const serializedParams = await serializeExceedParams(
		params,
		'pivotingFilters',
		9
	);
	const url = getApiUrl(
		`/analysis/mainchart${serializedParams ? `?${serializedParams}` : ''}`
	);
	return getRequest(url);
};

export const exportInitiatives = () => {
	const url = getApiUrl('/initiativesExport/initiatives');

	return getRequest(url);
};

export const getBreakdownSpendComparison = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/analysis/breakdownspendcomparison${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const getBreakdownComparisonInfo = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/analysis/breakdowncomparisoninfo${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const getCategoryTree = async params => {
	const serializedParams = await serializeExceedParams(
		params,
		'pivotingFilters',
		9
	);
	const url = getApiUrl(
		`/analysis/categorytree${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const exportVendorFragmentation = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/analysisexport/spendfragmentationchart${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const getIndustryBlends = () => {
	const url = getApiUrl('/IndustryBlends');
	return getRequest(url);
};

export const removeIndustryBlend = id => {
	const url = getApiUrl(`/IndustryBlends/${id}`);
	return deleteRequest(url);
};

export const updateIndustryBlends = data => {
	const url = getApiUrl('/IndustryBlends');
	const options = {
		body: JSON.stringify(data),
	};
	return putRequest(url, options);
};

export const getIndustryBlendIndustries = () => {
	const url = getApiUrl('/IndustryBlends/industries');
	return getRequest(url);
};

export const setDefaultIndustryBlend = id => {
	const url = getApiUrl(`/IndustryBlends/${id}`);
	return patchRequest(url);
};

export const getCategoryInsights = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/benchmark/get-analysis${serializedParams ? `?${serializedParams}` : ''}`
	);

	return getRequest(url);
};

export const getCategoryInsightsIndustryBlends = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/CategoryInsights/industryblends${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const exportCategoryInsightsSpendData = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/CategoryInsightsExport/spenddata${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const exportCategoryInsightsVendorCount = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/CategoryInsightsExport/vendorcount${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const exportCategoryInsightsShareofspends = params => {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/CategoryInsightsExport/shareofspends${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);

	return getRequest(url);
};

export const getCategoryInsightsCompanies = () => {
	const url = getApiUrl('/categoryinsights/companies');

	return getRequest(url);
};

export function downloadDeck(data = {}) {
	const url = getApiUrl('/exportppt');
	const options = {
		body: JSON.stringify(data),
	};

	return postRequest(url, options);
}
export function exportPowerPivotCSV(params) {
	const { pivotMode, filterModel } = params;
	const newparams = { ...params, filterModel: [filterModel] };
	const url = pivotMode
		? getApiUrl('/pivot-csv-export')
		: getApiUrl('/grouping-export-csv');
	const options = {
		body: JSON.stringify(newparams),
	};

	return postRequest(url, options);
}
export function exportNormlizationCSV(data = {}) {
	const url = getApiUrl('/normilization-export-csv');
	const options = {
		body: JSON.stringify(data),
	};
	return postRequest(url, options);
}
export function updateDataClassificationState(data) {
	const url = getApiUrl('/data-classification/update-normalization-state');
	const options = {
		body: JSON.stringify(data),
	};
	return postRequest(url, options);
}
export const getNormalizationUploadStatus = () => {
	const url = getApiUrl('/data-classification/get-normalization-state');

	return getRequest(url);
};

export function getVendorsByCategory(bodyParam) {
	const url = getApiUrl(`/d-and-i/vendors-by-category`);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getVendorsByUSStates(bodyParam) {
	const url = getApiUrl(`/d-and-i/usa-heatmap`);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getMultipleDiversity(bodyParam) {
	const url = getApiUrl(`/d-and-i/multiple-diversity`);
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getClientVSDiversedSpend(params) {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/SpendAnalysis/spend-analysis-amount${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function getClientVSDiversedVendor(params) {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/SpendAnalysis/spend-analysis-vendors${
			serializedParams ? `?${serializedParams}` : ''
		}`
	);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function createDAndIFile(file) {
	const url = getApiUrl(`/d-and-i/upload`);
	const options = {
		method: 'POST',
		body: JSON.stringify(file),
	};
	return postRequest(url, options);
}

export function validateDAndIFile(id) {
	const url = getApiUrl(`/d-and-i/${id}/validate`);
	const options = {
		method: 'POST',
	};
	return postRequest(url, options);
}

export function getClientDataUploadStatus() {
	const url = getApiUrl('/d-and-i/get-status');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getClientDataUploadHistory() {
	const url = getApiUrl('/d-and-i/get-history');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function deleteDandIData(value) {
	const url = getApiUrl(`/d-and-i/delete-data?deleteHistory=${value}`);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function getClientDataKPI(params) {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/d-and-i/get-kpi${serializedParams ? `?${serializedParams}` : ''}`
	);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function updateDandICertifications(bodyParam) {
	const url = getApiUrl('/d-and-i/edit-certification');
	const options = {
		body: JSON.stringify(bodyParam),
	};
	return putRequest(url, options);
}
// updateSpendEditData

//AP Vendor Library

export function getVendorCountByCategoryData(bodyParam) {
	const url = getApiUrl('/APVendorDB/chart-apvendor-by-category');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getTopVendorForApLibrary(bodyParam) {
	const url = getApiUrl('/APVendorDB/top15-apvendor-by-category');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

//For charts in Sku Analysis
export function getGeoCOGSData(bodyParam) {
	const url = getApiUrl('/sku/map');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getSkuKpiData(bodyParam) {
	const url = getApiUrl('/sku/kpi');
	const options = {
		method: 'POST',
		body: JSON.stringify(bodyParam),
	};
	return postRequest(url, options);
}

export function getDistinctSku(payload) {
	const url = getApiUrl('/sku/sku-filters');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}
export function validateSkuFile(file) {
	return createSkuFile(file).then(({ url, id }) =>
		uploadFile(url, file).then(
			() =>
				validateSkuFilev2(id, url).then(validationResponse => {
					return { id, url, validationResponse };
				})
			// .then(()=>{
			// 		executeSkuPipeline(id,url);
			// 	}
			// )
		)
	);
}

export function createSkuFile(file) {
	const url = getApiUrl(`/sku/fileupload`);
	const options = {
		method: 'POST',
		body: JSON.stringify(file),
	};
	return postRequest(url, options);
}

export function validateSkuFilev2(id, fileUrl) {
	const url = getApiUrl(`/sku/${id}/validate`);
	const options = {
		method: 'POST',
	};
	return postRequest(url, options);
}

export function executeSkuPipeline(id, fileUrl) {
	const payload = {
		id: id,
		url: fileUrl,
	};
	const url = getApiUrl(`/sku/execute-sp-pipeline`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}

export function cleanSkuState() {
	const url = getApiUrl('/sku/clear-sku-state');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getSkuFileArchiveData() {
	const url = getApiUrl('/sku/get-sku-files-history');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getMultiSourceSkuFilterData(payload) {
	const url = getApiUrl('/sku/filters');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}

export function getCascadingSkuFiltersData(payload) {
	const url = getApiUrl('/sku/cascading-sku-filters');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}

export function generateAnalysisModule(payload) {
	const url = getApiUrl(`/sku/save-filters`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}
export function updateFilterModule(payload) {
	const url = getApiUrl(`/sku/save-filters-module`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}
export function getSKUMapperFiles() {
	const url = getApiUrl('/sku/get-save-filters');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getVisualViewData(payload) {
	const url = getApiUrl('/sku/visual-sku');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}

export function getModuleFilter(payload) {
	const url = getApiUrl('/sku/filters-by-module');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
}
export function getModuleInfo(payload) {
	const url = getApiUrl(`/sku/sku-information-kpis`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}
export function deletetModuleInfo(payload) {
	const url = getApiUrl(`/sku/delete-filters-module`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}

export function getDatesSKU() {
	const url = getApiUrl('/sku/max-min-date');
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function getDeepDiveInfo(payload) {
	const url = getApiUrl(`/sku/deep-dive`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}

export function getCommodityHierarchy() {
	const url = getApiUrl(`/sku/commodity-hierarchy`);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function getTimeline(payload) {
	const url = getApiUrl(`/sku/timeline`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}

export function getTimelineTwo(payload) {
	const url = getApiUrl(`/sku/timeline-group-by`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}

export function getSuppliersForMarketAnalysis(payload) {
	const url = getApiUrl('/sku/sku-market-analysis-supplier-filter');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
}

export function getSerialNoForMarketAnalysis(payload) {
	const url = getApiUrl('/sku/sku-market-analysis-serial-no-filter');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
}

export function getSKUForMarketAnalysis(payload) {
	const url = getApiUrl('/sku/sku-market-analysis-sku-filter');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
}

export function getChartDataForMarketAnalysis(payload) {
	const url = getApiUrl('/sku/sku-market-analysis');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
}

export function deleteSkuData() {
	const url = getApiUrl('/sku/delete-sku-data');
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getSkuCustomColumns() {
	const url = getApiUrl('/sku/get-custom-column');
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function saveSkuCustomColumns(payload) {
	const url = getApiUrl('/sku/update-custom-column');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}

export function getTimelineSettings(payload) {
	const url = getApiUrl('/sku/get-timeline-settings');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}

export function saveTimelineSettings(payload) {
	const url = getApiUrl(`/sku/save-timeline-settings`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}

export function getCatgoriesForFilter() {
	const url = getApiUrl('/analysis/categories');
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

///SPEND CONTROLLER APIS
export function getBaselineSummaryKpis(payload) {
	const url = getApiUrl('/spend-c/baseline-summary-statistics');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload || {}),
	};

	return postRequest(url, options);
}

export function getWorkshopId() {
	const url = getApiUrl(`/spend-c/workshop-id`);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function updateWorkshopId(payload) {
	const url = getApiUrl('/spend-c/update-workshop-id');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload || {}),
	};

	return postRequest(url, options);
}

export function getPreWorkShopSummaryKpis(payload) {
	const url = getApiUrl('/spend-c/pre-workshop-live-status');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload || {}),
	};

	return postRequest(url, options);
}

export function getSpendControllerFilters(payload) {
	const url = getApiUrl(`/spend-c/filters`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}
export function getDistinctValue(payload) {
	const url = getApiUrl(`/spend-c/distinct-filters`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}
export function updateSpendControllerBaseline(payload) {
	const url = getApiUrl('/spend-c/edit');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload || {}),
	};

	return postRequest(url, options);
}

export function importSpendController(formData, isAppend) {
	const url = getApiUrl(
		isAppend ? `/spend-c/upload/append` : '/spend-c/upload'
	);
	const options = {
		method: 'POST',
		body: formData,
	};

	return postRequest(url, options, true);
}
export function exportSpendOwners(type) {
	const url = getApiUrl(`/spend-c/excel/export`);
	const options = {
		method: 'POST',
		body: JSON.stringify({
			filters: {
				businessUnit: [],
				entity: [],
				spendOwner: [],
				finalCategory: [],
				normalizedVendor: [],
			},
			type: type,
		}),
	};

	return postRequest(url, options);
}

export function exportSpendController(payload) {
	const url = getApiUrl(`/spend-c/excel/export`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
}
export function importSpendOwners(formData, path) {
	const url = getApiUrl(path);
	const options = {
		method: 'POST',
		body: formData,
	};

	return postRequest(url, options, true);
}
export function sendMailtoSpendOwners(formData) {
	const url = getApiUrl('/spend-c/SendEmail');
	const options = {
		method: 'POST',
		body: formData,
	};

	return postRequest(url, options, true);
}
export function getWorkshopName(params) {
	const serializedParams = serializeQueryParams(params);
	const url = getApiUrl(
		`/spend-c/workshop-name${serializedParams ? `?${serializedParams}` : ''}`
	);
	const options = {
		method: 'GET',
	};

	return getRequest(url, options);
}

export function getPreWorkshopBaselineSummaryKpis(payload) {
	const url = getApiUrl('/spend-c/pre-workshop-spend-summary-by-vendor-action');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload || {}),
	};

	return postRequest(url, options);
}

export function getMicroTailBaselineSummaryKpis(payload) {
	const url = getApiUrl('/spend-c/micro-tail-spend-summary');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload || {}),
	};

	return postRequest(url, options);
}
export function getMicroTailVendorActionKpis(payload) {
	const url = getApiUrl('/spend-c/micro-tail-spend-recommended-action');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload || {}),
	};

	return postRequest(url, options);
}
export function getMicroTailPreliminarySavingsSummary(payload) {
	const url = getApiUrl('/spend-c/micro-tail-preliminary-savings-summary');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload || {}),
	};

	return postRequest(url, options);
}

export function getPreWorkshopPreliminarySavingsSummary(payload) {
	const url = getApiUrl('/spend-c/pre-workshop-preliminary-savings-summary');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload || {}),
	};

	return postRequest(url, options);
}

export function getWorkshopSummaryKpis(payload) {
	const url = getApiUrl('/spend-c/workshop-preliminary-saving-summary');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload || {}),
	};

	return postRequest(url, options);
}

export function getSavingSignOffSummaryKpis(payload) {
	const url = getApiUrl('/spend-c/saving-sign-off');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload || {}),
	};

	return postRequest(url, options);
}

export function getDashBoardWaterFall(payload) {
	const url = getApiUrl(
		`/spend-c/final-baseline-by-action${
			payload !== null ? '?search=' + payload.name : ''
		}`
	);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function getDashBoardPie(payload) {
	const url = getApiUrl(`/spend-c/final-savings-by-action`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}

export function getVendorCount(payload) {
	const url = getApiUrl(
		`/spend-c/final-vendor-count${
			payload !== null ? '?search=' + payload.name : ''
		}`
	);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function getProgramSavingsMainTable(payload) {
	const url = getApiUrl(
		`/spend-c/program-savings-summary${
			payload !== null ? '?search=' + payload.name : ''
		}`
	);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function getProgramSavingsMicroMacroTable(payload) {
	const url = getApiUrl(
		`/spend-c/incremental-savings-summary${
			payload !== null ? '?search=' + payload.name : ''
		}`
	);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function getVendorEliminationReport(payload) {
	const url = getApiUrl(
		`/spend-c/vendor-elimination-report${
			payload !== null ? '?search=' + payload.name : ''
		}`
	);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function getVendorReductionReport(payload) {
	const url = getApiUrl(
		`/spend-c/vendor-reduction-report${
			payload !== null ? '?search=' + payload.name : ''
		}`
	);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function exportGLMasterData() {
	const url = getApiUrl(`/spend-c/excel/export`);
	const options = {
		method: 'POST',
		body: JSON.stringify({
			filters: {
				businessUnit: [],
				entity: [],
				spendOwner: [],
				finalCategory: [],
				normalizedVendor: [],
			},
			type: 0,
		}),
	};

	return postRequest(url, options);
}

export function deleteSpendControllerData() {
	const url = getApiUrl(`/spend-c/delete-Baseline-Data`);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}
///Survey Form Apis Start
export function getAliasAccordingToEnv() {
	const url = getApiUrl(`/SurveyForm/get-all-alias-env`);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}
export function saveOrUpdateSurveyForm(payload) {
	const url = getApiUrl(`/SurveyForm/Save-Survey-Form`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
}
export function getSurveyForm(payload) {
	const url = getApiUrl(`/SurveyForm/get-survey-Form`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
}

export function getAliasList() {
	const url = getApiUrl(`/SurveyForm/get-all-alias-list`);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function sendEmailtoAliasOwners(payload) {
	const url = getApiUrl(`/SurveyForm/send-email`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
}
export function updateMailforAlias(payload) {
	const url = getApiUrl(`/SurveyForm/update-emailId-alias`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
}
///Survey Form Apis End
export function getIndustryArticles(payload) {
	const url = getApiUrl(`/landingpage/get-industry-news-article`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
}

export const getVendorArticles = () => {
	const url = getApiUrl('/landingpage/get-vendor-new-article');

	return getRequest(url);
};

export const getSXRStats = () => {
	const url = getApiUrl('/landingpage/get-stats-data');

	return getRequest(url);
};

export const getKPI = payload => {
	const url = getApiUrl('/landingpage/get-kpi-data');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
};

export const getLegalRevenueGrowth = () => {
	const url = getApiUrl('/legal/get-revenue');

	return getRequest(url);
};

export const getLegalNetIncomeGrowth = () => {
	const url = getApiUrl('/legal/get-net-operating-income');

	return getRequest(url);
};

export const getLegalProfitLawyerGrowth = () => {
	const url = getApiUrl('/legal/get-profit-per-lawyer');

	return getRequest(url);
};

export const getMinMaxAvgByRoleFilters = payload => {
	const url = getApiUrl('/legal/legal-filters');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
};

export const getMinMaxAvgRoleDate = payload => {
	const url = getApiUrl('/legal/chart-by-role');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
};

// generate spend cube file upload

export function createFileDirectory(filesList) {
	const url = getApiUrl(`/column-mapping/file-upload`);
	const options = {
		method: 'POST',
		body: JSON.stringify(filesList),
	};
	return postRequest(url, options);
}

export function getUploadPath(filesList) {
	return createFileDirectory(filesList);
}

export function getEtlState() {
	const url = getApiUrl('/column-mapping/etl-state');
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function updateEtlState(state) {
	const url = getApiUrl('/column-mapping/update-state');
	const options = {
		method: 'POST',
		body: JSON.stringify(state),
	};
	return postRequest(url, options);
}

export function postUploadedUrls(files) {
	const url = getApiUrl(`/column-mapping/data-upload`);
	const options = {
		method: 'POST',
		body: JSON.stringify(files),
	};
	return postRequest(url, options);
}

export function getColumnHeaders() {
	const url = getApiUrl(`/column-mapping/preview`);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function postMappedJson(mappedJson) {
	const url = getApiUrl(`/column-mapping/mapped-json`);
	const options = {
		method: 'POST',
		body: JSON.stringify(mappedJson),
	};
	return postRequest(url, options);
}

export function restartProcess() {
	const url = getApiUrl(`/column-mapping/restart-uploading`);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function filesProcesing() {
	const url = getApiUrl(`/column-mapping/files-processing`);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function getEtlFiles() {
	const url = getApiUrl(`/column-mapping/get-etl-files`);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function getExcludedSummary() {
	const url = getApiUrl(`/column-mapping/excluded-summary`);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function triggerNormailzation(isAutomation) {
	const url = getApiUrl(
		`/column-mapping/validate-new/${isAutomation ? '1' : '0'}`
	);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function triggerClassification() {
	const url = getApiUrl(`/column-mapping/validate-classification`);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}

export function downloadEtlFile(file) {
	const url = getApiUrl(`/column-mapping/download-files?type=${file}`);
	const options = {
		method: 'GET',
	};
	return getRequest(url, options);
}


export const getVideoMetaData = () => {
	const url = getApiUrl('/landingpage/get-video-metadata');

	return getRequest(url);
};
export const getBreakDownFilterForCapitalMarket = payload => {
	const url = getApiUrl(
		`/market-capital/breakdown?dimension=${payload.dimension}&dateFrom=${
			payload.dateFrom
		}&dateTo=${payload.dateTo}`
	);

	return getRequest(url);
};
export const getBreakDownFilterColumnsForCapitalMarket = () => {
	const url = getApiUrl(`/market-capital/filters`);

	return getRequest(url);
};
export const getBoxPlotChartDataCapitalMarket = payload => {
	const url = getApiUrl(
		`/market-capital/boxplot?dimension=${payload.dimension}&dateFrom=${
			payload.dateFrom
		}&dateTo=${payload.dateTo}`
	);

	return getRequest(url);
};

export const getLineChartChartDataCapitalMarket = payload => {
	const url = getApiUrl(
		`/market-capital/linechart?dimension=${payload.dimension}&dateFrom=${
			payload.dateFrom
		}&dateTo=${payload.dateTo}`
	);

	return getRequest(url);
};

export function getDrillDownVendorData(payload) {
	const url = getApiUrl(`/market-capital/drilldown-vendor`);
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
}
export function updateCapitalMarket(payload) {
	const url = getApiUrl('/market-capital/edit');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload || {}),
	};

	return postRequest(url, options);
}

export const getSavingsCalculationData = payload => {
	const url = getApiUrl('/market-capital/get-summary');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};
	return postRequest(url, options);
};

export const getCapitalMarketDateRange = payload => {
	const url = getApiUrl('/market-capital/get-date-range');
	return getRequest(url);
};

export function updateCapitalMarketSettings(payload) {
	const url = getApiUrl('/market-capital/filters');
	const options = {
		method: 'PATCH',
		body: JSON.stringify(payload || {}),
	};

	return patchRequest(url, options);
}

export const getThresholdCapitalMarket = () => {
	const url = getApiUrl('/market-capital/threshold');
	return getRequest(url);
};

export const editThresholdCapitalMarket = payload => {
	const url = getApiUrl('/market-capital/edit-threshold');
	const options = {
		method: 'PATCH',
		body: JSON.stringify(payload || {}),
	};

	return patchRequest(url, options);
};

export const getThresholdSavingsCalculation = () => {
	const url = getApiUrl('/market-capital/savings-threshold');
	return getRequest(url);
};

export const editThresholdSavingsCalculation = payload => {
	const url = getApiUrl('/market-capital/edit-savings-threshold');
	const options = {
		method: 'PATCH',
		body: JSON.stringify(payload || {}),
	};

	return patchRequest(url, options);
};

export const getCapitalMarketBenchmarkVendorAnalysis = payload => {
	const url = getApiUrl('/market-capital/vendor-analysis');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
};

export const getCostPerLawyerGrowth = () => {
	const url = getApiUrl('/legal/get-cost-per-lawyer');

	return getRequest(url);
};

export const getTopFirmName = () => {
	const url = getApiUrl('/legal/get-top-firmname');

	return getRequest(url);
};

export const getLegalFirmnames = () => {
	const url = getApiUrl('/legal/get-firmname');

	return getRequest(url);
};

export const getCostPerLawyerByFirmName = payload => {
	const url = getApiUrl('/legal/get-cpl-by-firmname');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
};

export const getCapitalMarketBenchmarkCategoryAnalysis = payload => {
	const url = getApiUrl('/market-capital/category-analysis');
	const options = {
		method: 'POST',
		body: JSON.stringify(payload),
	};

	return postRequest(url, options);
};

export const getBreakDownFilterColumnsForCapitalMarketBenchmark = () => {
	const url = getApiUrl(`/market-capital/bm-filters`);

	return getRequest(url);
};

export const getBreakDownFilterForCapitalMarketBenchmark = payload => {
	const url = getApiUrl(
		`/market-capital/vendor-filter-breakdown?dimension=${payload.dimension}`
	);

	return getRequest(url);
};
